<template>
  <div>
    <div v-if="loadingSubscription" class="grow flex items-center justify-center my-36">
      <Loader />
    </div>
    <div v-else class="p-5 pt-0 bg-app-white dark:bg-app-dark-surface1">
      <div v-if="subscriptionDowngrade">
        <Alerts variant="info" class="mb-4" show-icon>
          <template #alertMessage>
            <div class="flex flex-col sm:flex-row items-center justify-between grow gap-2">
              <p class="text-sm font-medium text-app-blue-500">
                Your current plan remains unchanged for now until
                <strong>
                  {{
                    new Date(subscriptionDowngrade.start_date * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
                  }}.
                </strong>
                After that, you'll be downgraded to
                {{ capitalize(subscriptionDowngrade.to_plan) }}
                plan.
              </p>
              <Button
                variant="secondary"
                class="text-base! font-medium! text-app-gray-900! no-underline w-full! sm:w-auto!"
                :disabled="cancelingDowngrade"
                :loading="cancelingDowngrade"
                @click="cancelDowngrade"
              >
                <div class="text-sm">Cancel Downgrade</div>
              </Button>
            </div>
          </template>
        </Alerts>
      </div>
      <div class="grid sm:grid-cols-2 gap-4 mb-12">
        <Card class="p-4 flex flex-col gap-6" :shadow="false">
          <h4 class="text-2xl font-bold text-app-gray-900 flex items-center gap-2">
            {{ planMap[currentPlan].name }}
          </h4>
          <p class="text-sm font-normal text-app-gray-600">
            Switch your subscription to a different type, such as a monthly plan, annual plan, or enterprise plan.
          </p>
          <div class="flex items-center gap-4">
            <Button class="flex gap-2" @click="scrollToPlan">
              <Icon name="document-text-solid-icon" />
              Change Plan
            </Button>
            <Button variant="secondary" class="text-base! font-medium! text-app-gray-900! no-underline" @click="trackComparePlans">
              Compare Plans
            </Button>
          </div>
        </Card>
        <Card class="p-4 flex flex-col gap-6" :shadow="false">
          <h4 class="text-xl font-bold text-app-gray-500">Your Subscription Includes:</h4>
          <div class="grid grid-cols-1 gap-2">
            <ul class="p-0 m-0 sm:flex flex-col sm:flex-row flex-wrap">
              <li v-for="(item, idx) in planMap[currentPlan].items" :key="idx" class="list-item">
                <span class="flex gap-2">
                  <div>
                    <Icon class="text-app-green-400!" :name="item.icon" />
                  </div>
                  <span v-dompurify-html="item.text" class="text-[14px] sm:text-sm font-normal text-app-gray-500" />
                </span>
              </li>
            </ul>
          </div>
        </Card>
      </div>

      <div class="pb-12 border-b border-app-gray-200">
        <h2 class="text-2xl sm:text-3xl font-extrabold mt-5 text-app-gray-900 mb-5">Subscription Usage</h2>
        <Card :shadow="false" class="p-5 mb-4">
          <div class="text-lg sm:text-2xl text-app-gray-900 flex items-center justify-between">
            <div class="flex items-center">
              <h2 class="font-bold mr-2">Total MAW Usage</h2>
              <Popover position="top" is-dark>
                <Icon name="information-circle-icon" class="text-app-gray-400"></Icon>
                <template #popover-content>
                  <div class="text-sm sm:text-base font-normal text-app-gray-400 w-max max-w-[350px] p-4">
                    <div class="text-app-white">What is MAW?</div>
                    <p class="text-sm mt-2">
                      1 Monthly Active Wallet (MAW) represents a user who logs in or has an active session at least once in a given calendar month,
                      regardless of the number of times a user logs in during that month.
                    </p>
                  </div>
                </template>
              </Popover>
            </div>
            <Loader v-if="loadingMau" :size="30" />
            <p v-else class="font-semibold">{{ totalMAU }} MAW</p>
          </div>
        </Card>
        <Card :shadow="false" class="p-5">
          <div class="text-2xl text-app-gray-900 flex items-center justify-between">
            <div class="flex items-center">
              <h2 class="font-bold mr-2">Total SMS Usage</h2>
              <Popover position="top" is-dark>
                <Icon name="information-circle-icon" class="text-app-gray-400"></Icon>
                <template #popover-content>
                  <div class="text-sm sm:text-base font-normal text-app-gray-400 w-max max-w-[350px] p-4">
                    <div class="text-app-white">How is SMS Usage charged?</div>
                    <p class="text-sm mt-2">
                      Additional charges applies per login based on regional rates if you select Mobile + OTP as a login method on Mainnet.
                      <a
                        href="https://airtable.com/shrRMmEe1uenTlmGD/tbl18TLNTObZQRbTH/viwJkYzyqZanZJzgg"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="cursor-pointer underline"
                      >
                        View regional charges here.
                      </a>
                    </p>
                  </div>
                </template>
              </Popover>
            </div>
            <Loader v-if="loadingMau" :size="30" />
            <span v-else class="font-semibold">{{ totalSMS }} Logins</span>
          </div>
        </Card>
      </div>

      <div id="changePlan" class="pb-[74px] border-b border-app-gray-200">
        <h2 class="text-3xl font-extrabold mt-5 text-app-gray-900 mb-2">Our pricing plans made simple</h2>
        <p class="text-lg font-normal text-app-gray-500 mb-6">
          All types of businesses need access to development resources, so we give you the option to decide how much you need to use.
        </p>
        <!-- <div class="flex gap-4 items-center mb-9">
          <span class="text-base font-medium text-app-gray-900">Monthly</span>
          <Toggle v-model="isPlanToggledToYearly" label-enabled="Yearly (Save 20%)" label-disabled="Yearly (Save 20%)" size="small" show-label />
        </div> -->
        <div class="grid sm:grid-cols-2 md:grid-cols-3 gap-16">
          <Card v-for="(planData, plan, idx) in selectablePlansMap" :key="idx" class="p-8 md:h-fit!" :shadow="false">
            <h4 class="text-2xl font-semibold text-app-gray-500 flex items-center gap-1 mb-4">
              {{ planData.name }}
              <Badge v-if="plan === SUBSCRIPTION_PLAN.scale && planData.order > currentPlanOrder" variant="info">POPULAR</Badge>
            </h4>
            <h5 v-if="plan !== SUBSCRIPTION_PLAN.enterprise" class="flex items-baseline mb-4">
              <span class="text-xl font-semibold text-app-gray-900">$</span>
              <span class="text-3xl font-extrabold text-app-gray-900">
                {{ isPlanToggledToYearly ? planData.yearlyPrice : planData.monthlyPrice }}
              </span>
              <span class="text-lg font-medium text-app-gray-500">{{ isPlanToggledToYearly ? "/year" : "/month" }}</span>
            </h5>
            <h5 v-else class="text-3xl font-extrabold text-app-gray-900 mb-4">{{ planData.title }}</h5>
            <p class="text-base font-normal text-app-gray-500">{{ planData.description }}</p>
            <ul class="p-0 m-0 flex flex-col gap-5 mt-8">
              <li v-for="(item, itemIdx) in planData.items" :key="itemIdx" class="flex gap-3">
                <div>
                  <Icon class="text-app-green-400!" :name="item.icon" />
                </div>
                <div v-dompurify-html="item.text" class="text-sm font-normal text-app-gray-500" />
              </li>
            </ul>

            <div class="w-full mt-16">
              <CustomTooltip
                v-if="[SUBSCRIPTION_PLAN.base, SUBSCRIPTION_PLAN.growth, SUBSCRIPTION_PLAN.scale].includes(plan)"
                placement="top"
                theme="dark"
                class="w-full"
                :hide="!Boolean(subscriptionDowngrade)"
              >
                <Button variant="primary" :disabled="Boolean(subscriptionDowngrade)" pill block @click="openChangePlanModal(plan)">
                  Get Started
                </Button>
                <template #content>
                  <div class="text-sm py-2 px-1 max-w-[240px] text-center">This option is unavailable during a pending downgrade.</div>
                </template>
              </CustomTooltip>

              <Button v-else variant="secondary" pill block @click="openEnterprisePlanCalendly">Contact Sales</Button>
            </div>
          </Card>
        </div>
      </div>

      <div class="pt-5 text-sm font-normal text-app-gray-500">
        <b>*1 Monthly Active Wallet (MAW)</b>
        represents a user who logs in or has an active session at least once in a given calendar month, regardless of the number of times a user logs
        in during that month.
      </div>
    </div>
    <ChangePlanModal
      :coupon-code="couponCode"
      :stripe-elements="stripeElements"
      :stripe-load-error="stripeError"
      :open="showChangePlanModal"
      :plan="selectedPlanDetails"
      :confirming-subscription="confirmingSubscription"
      :is-yearly-selected="isPlanToggledToYearly"
      :sla-shown="slaShown"
      :credit-card="creditCard"
      :selected-team-id="selectedTeamId"
      :plan-name="newPlan"
      :payment-error="paymentError"
      @update:coupon-code="onChangeCouponCode"
      @confirmSubscription="confirmSubscription"
      @resetPaymentError="onResetPaymentError"
      @on-close="onClose"
    />
    <DowngradePlanModal
      :selected-team-id="selectedTeamId"
      :open="Boolean(downgradePlan)"
      :current-plan="currentPlan"
      :downgrade-plan="downgradePlan"
      @close="downgradePlan = ''"
    />
  </div>
</template>

<script lang="ts">
import { Badge } from "@toruslabs/vue-components/Badge";
import { Icon } from "@toruslabs/vue-components/Icon";
import { Loader } from "@toruslabs/vue-components/Loader";
import { capitalize, defineComponent } from "vue";

import { Alerts } from "@/components/Alerts";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import CustomTooltip from "@/components/CustomTooltip";
import Popover from "@/components/Popover";
import { useStripe } from "@/composables/useStripe";
import config from "@/config";
import { ANALYTICS_CTA_TYPES, orgAnalytics, ORGANIZATION_ANALYTICS_EVENTS } from "@/helpers/analytics";
import { trackBillingPlanChangePage } from "@/helpers/gtmSubscriptionVirtualPages";
import log from "@/helpers/log";
import { billingModule, teamModule, userModule } from "@/store";
import { SUBSCRIPTION_PLAN, SUBSCRIPTION_PLAN_INFO, TOAST_TYPES } from "@/utils/enums";
import { Plan } from "@/utils/interface";
import { delay, numberWithCommas } from "@/utils/utils";

import { getStripeClient, stripeDarkAppearance } from "../../../../lib/stripeClient";
import ChangePlanModal from "./ChangePlanModal.vue";
import DowngradePlanModal from "./DowngradePlanModal.vue";

export default defineComponent({
  name: "PlanAndSubscription",
  components: {
    Alerts,
    Badge,
    Button,
    Card,
    ChangePlanModal,
    Icon,
    Loader,
    Popover,
    DowngradePlanModal,
    CustomTooltip,
  },
  props: {
    selectedTeamId: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const { stripe, stripeError } = useStripe();
    return {
      capitalize,
      stripe,
      stripeError,
    };
  },
  data: () => ({
    error: null as string | null,
    paymentError: "" as string,
    clientSecret: null as string | null,
    yearlyMode: false,
    changePlanAgreement: false,
    newPlan: "",
    confirmingSubscription: false,
    slaShown: false,
    selectedSLAPlan: "",
    showChangePlanModal: false,
    SUBSCRIPTION_PLAN,
    isPlanToggledToYearly: false,
    selectedPlanDetails: {},
    couponCode: "",
    downgradePlan: "",
    downgrading: false,
    cancelingDowngrade: false,
  }),
  computed: {
    team() {
      return teamModule.selectedTeamById(this.selectedTeamId);
    },
    subscription() {
      return billingModule.getSubscription(this.selectedTeamId);
    },
    subscriptionDowngrade() {
      return billingModule.getSubscriptionDowngrade(this.selectedTeamId);
    },
    hasSubscription() {
      return Boolean(this.subscription && this.subscription?.customer && this.subscription.customer?.subscriptions.length > 0);
    },
    loadingSubscription() {
      return !billingModule.getSubscriptionLoaded(this.selectedTeamId);
    },
    stripeElements() {
      // Stripe.js is warmed at the dashboard level (Home.vue) but loaded lazily. Gate on the
      // reactive `stripe` client (null until loaded) so this re-evaluates once it resolves,
      // rather than assuming it's ready just because the setup intent returned.
      if (this.clientSecret && this.stripe) {
        return this.stripe.elements({
          clientSecret: this.clientSecret,
          appearance: stripeDarkAppearance,
        });
      }
      return null;
    },
    creditCard() {
      const { card } = this.subscription;
      if (card) {
        const { brand, last4, expiryMonth, expiryYear } = card;
        return {
          brand: capitalize(brand),
          last4,
          expiryMonth,
          expiryYear,
        };
      }
      return null;
    },
    currentPlan() {
      return this.subscription?.customer?.plan || "";
    },
    planMap(): Record<string, Plan> {
      return { ...SUBSCRIPTION_PLAN_INFO };
    },
    selectablePlansMap(): Record<string, Plan> {
      // return the plan key value pairs that are not the current plan
      return Object.entries(this.planMap)
        .filter(([key]) => key !== this.currentPlan)
        .reduce(
          (acc, [planKey, planData]) => ({
            ...acc,
            [planKey]: planData,
          }),
          {}
        );
    },
    elementsOptions() {
      return {
        appearance: {},
        clientSecret: this.clientSecret,
      };
    },
    isLegacyPlan() {
      return !Object.values(SUBSCRIPTION_PLAN).includes(this.currentPlan);
    },
    currentPlanOrder() {
      if (this.isLegacyPlan) return 0;
      return this.planMap[this.currentPlan].order;
    },
    totalMAU() {
      const { mau } = billingModule.getMauUsage(this.selectedTeamId) ?? {};
      return numberWithCommas(mau ?? 0);
    },
    totalSMS() {
      const { sms } = billingModule.getMauUsage(this.selectedTeamId) ?? {};
      return numberWithCommas(sms ?? 0);
    },
    loadingMau() {
      return billingModule.getMauUsage(this.selectedTeamId) === undefined;
    },
  },
  async created() {
    if (billingModule.getMauUsage(this.selectedTeamId) === undefined) {
      await billingModule.fetchMauUsage({ teamId: this.selectedTeamId });
    }
    billingModule.fetchSubscriptionDowngrade({ teamId: this.selectedTeamId });
  },
  methods: {
    getPlanWithInterval(plan: string) {
      return `${plan}_${this.yearlyMode ? "yearly" : "monthly"}`;
    },
    async confirmSubscription() {
      // analytics
      const trackData = {
        is_upgrade: true,
        old_plan: this.currentPlan,
        new_plan: this.newPlan,
      };

      try {
        this.paymentError = "";
        this.confirmingSubscription = true;
        if (!this.creditCard && this.stripeElements) {
          await billingModule.addCard({ stripeElements: this.stripeElements, teamId: this.selectedTeamId });
        }
        const subscriptionPlan = this.getPlanWithInterval(this.newPlan);
        const trimmedCouponCode = this.couponCode.trim();
        const couponPayload = trimmedCouponCode ? { couponCode: trimmedCouponCode } : {};
        let result;
        if (this.hasSubscription) {
          result = await billingModule.updateSubscription({
            teamId: this.selectedTeamId,
            subscriptionPlan,
            ...couponPayload,
          });
        } else {
          result = await billingModule.createSubscription({
            teamId: this.selectedTeamId,
            subscriptionPlan,
            ...couponPayload,
          });
        }
        /**
         * handling (double) 3d secure scenario
         * Normally it should already be handled during card confirmSetup above
         * But for some cards we face the situation where we need to confirm
         * 3d secure again for the transaction itself
         *
         * Most other cards do not require further 3d secure confirmation after
         * the initial card setup
         */
        if (result.client_secret) {
          const stripeClient = getStripeClient();
          const { error } = await stripeClient.confirmPayment({
            clientSecret: result.client_secret,
            redirect: "if_required",
          });
          if (error) throw error;
        }

        // analytics
        orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.PLAN_CHANGE_COMPLETED, trackData);
        trackBillingPlanChangePage("upgradeSuccess");

        /**
         * delay before fetching latest subscription so we have enough time for
         * webhook to update subscription plan to latest (e.g. growth, scale, etc.)
         */
        await delay(2000);
        await Promise.all([
          billingModule.fetchSubscription({ teamId: this.selectedTeamId }),
          billingModule.getInvoices({ teamId: this.selectedTeamId }),
        ]);
        userModule.setToastMessage({ message: "Change of subscription plan successful.", type: TOAST_TYPES.SUCCESS });
        this.closeSubscriptionModal();
        this.scrollToTop();
      } catch (e) {
        log.error(e);

        // analytics
        orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.PLAN_CHANGE_FAILED, {
          ...trackData,
          error_message: (e as Error).message,
        });

        this.paymentError = (e as Error).message;
      } finally {
        this.confirmingSubscription = false;
      }
    },
    async choosePlan(plan: string) {
      this.newPlan = plan;
      this.showChangePlanModal = true;
      this.selectedPlanDetails = this.planMap?.[plan];
      if (!this.creditCard) {
        const setupIntent = await billingModule.createSetupIntent({ teamId: this.selectedTeamId });
        this.clientSecret = setupIntent.client_secret;
      }
    },
    closeSubscriptionModal() {
      // reset values
      this.showChangePlanModal = false;
      this.changePlanAgreement = false;
      this.clientSecret = "";
      this.paymentError = "";
      this.couponCode = "";
    },
    onClose() {
      this.showChangePlanModal = false;
      this.couponCode = "";
    },
    onChangeCouponCode(value: string) {
      this.couponCode = value;
      this.paymentError = "";
    },
    onResetPaymentError() {
      this.paymentError = "";
    },
    openChangePlanModal(plan: string) {
      const isUpgrade = this.planMap[plan].order > this.currentPlanOrder;
      if (isUpgrade) {
        this.choosePlan(plan);
      } else {
        this.handleDowngradePlan(plan);
      }
    },
    handleDowngradePlan(plan: string) {
      if (this.currentPlan === SUBSCRIPTION_PLAN.enterprise) {
        window.open(config.downgradePlanLink, "_blank", "noopener, noreferrer");
      } else {
        this.downgradePlan = plan;
      }
    },
    openEnterprisePlanCalendly() {
      window.open(config.enterprisePlanCalendlyLink, "_blank", "noopener, noreferrer");
    },
    scrollToPlan() {
      if (document) document?.getElementById("changePlan")?.scrollIntoView();
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    },
    trackComparePlans() {
      const url = "https://metamask.io/developer/embedded-wallets#pricing";
      orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.CTA_CLICKED, {
        cta_type: ANALYTICS_CTA_TYPES.DOCS_REFERENCE,
        url,
        feature_context: "plan-and-subscription",
      });
      window.open(url, "_blank", "noopener, noreferrer");
    },
    async cancelDowngrade() {
      try {
        this.cancelingDowngrade = true;
        await billingModule.cancelDowngrade({ teamId: this.selectedTeamId, scheduleId: this.subscriptionDowngrade.schedule_id });
        userModule.setToastMessage({ message: "Downgrade cancelled", type: TOAST_TYPES.SUCCESS });

        orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.PLAN_CHANGE_CANCEL_COMPLETED, {
          is_upgrade: false,
          old_plan: this.subscriptionDowngrade.from_plan,
          new_plan: this.subscriptionDowngrade.to_plan,
        });
      } catch (e) {
        log.error(e);
        orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.PLAN_CHANGE_CANCEL_FAILED, {
          is_upgrade: false,
          old_plan: this.subscriptionDowngrade.from_plan,
          new_plan: this.subscriptionDowngrade.to_plan,
        });
        userModule.setToastMessage({ message: "Failed to cancel downgrade", type: TOAST_TYPES.ERROR });
      } finally {
        this.cancelingDowngrade = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.list-item {
  flex: 0 0 50%;
  margin-bottom: 0.7rem;
}
</style>
